import {
  AloftDate,
  AloftPrice,
  MLSListing,
  StandardStatus,
  ToolkitPropertyEnum,
  listingStatusToUADStatus,
} from '@aloft/toolkit-shared';
import { Block, Flex, Typography } from '@aloft/toolkit-sui';

import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PropertyTooltip, PropertyTooltipProps } from '../../../components/PropertyTooltip.tsx';
import { getPropertyPinIcon } from '../../../components/Tkv2Map/utils/getPropertyPinIcon.ts';
import MapBox from '../../../components/map/MapBox.ts';
import { StyleRadio } from '../../../components/map/StyleRadio.js';
import { INITIAL_STYLE, MAP_LAYERS, MAP_SOURCES } from '../../../components/map/constants.js';
import { MapPopup } from '../../adjustPairedSalesStandalone/components/PairedSalesMap/MapPopup.tsx';
import { getBathsTotalInteger } from '../../referenceView/MLSDataSheet/utils/getBathsTotalInteger.ts';

const PLACEHOLDER = '-';

const propertyTooltipPropsFromListing = (listing: MLSListing): PropertyTooltipProps => {
  const listingStatus = listingStatusToUADStatus(listing.StandardStatus as StandardStatus);
  const isSold = listingStatus === 'SettledSale';
  const beds = listing.BedroomsTotal?.toString() ?? PLACEHOLDER;
  const baths = getBathsTotalInteger(listing);
  const gla = listing.LivingArea?.toLocaleString('en-US') ?? PLACEHOLDER;
  const address1 = listing.UnparsedAddress ?? '';
  const address2 = [listing.City, listing.StateOrProvince, listing.PostalCode]
    .filter(Boolean)
    .join(', ');
  const addressParts: readonly [string, string] = [address1, address2];
  const closeDate = listing.CloseDate ? AloftDate.fromIsoString(listing.CloseDate) : undefined;
  const salePrice = listing.ClosePrice
    ? AloftPrice.fromNumericPrice(listing.ClosePrice)
    : undefined;
  const listPrice = listing.ListPrice ? AloftPrice.fromNumericPrice(listing.ListPrice) : undefined;
  const photoUrl = listing.Media?.[0]?.MediaURL;

  return { isSold, beds, baths, gla, addressParts, closeDate, salePrice, listPrice, photoUrl };
};

type RegressionMapProps = {
  listings: MLSListing[];
};

export const RegressionMap = ({ listings }: RegressionMapProps) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapBox | null>(null);

  const [mapStyle, setMapStyle] = useState(INITIAL_STYLE.url);
  const [selectedListing, setSelectedListing] = useState<MLSListing | null>(null);

  const center = useMemo(() => {
    const valid = listings.filter((l) => l.Latitude && l.Longitude);
    if (valid.length === 0) return { lat: 39.5, lng: -98.35 };
    const lat = valid.reduce((sum, l) => sum + (l.Latitude ?? 0), 0) / valid.length;
    const lng = valid.reduce((sum, l) => sum + (l.Longitude ?? 0), 0) / valid.length;
    return { lat, lng };
  }, [listings]);

  // Build GeoJSON features using the correct sprite icon names (e.g. "Sold-1")
  const features = useMemo(() => {
    return listings
      .filter((l) => l.Latitude && l.Longitude)
      .map((l) => {
        const listingStatus = listingStatusToUADStatus(l.StandardStatus as StandardStatus);
        const icon = getPropertyPinIcon(
          listingStatus as ToolkitPropertyEnum<'ListingStatusType'>,
          false,
        );
        return {
          type: 'Feature' as const,
          geometry: {
            type: 'Point' as const,
            coordinates: [l.Longitude!, l.Latitude!],
          },
          properties: {
            icon,
            id: l.ListingId ?? `${l.Latitude}-${l.Longitude}`,
          },
        };
      });
  }, [listings]);

  const listingsById = useMemo(() => {
    const byId: Record<string, MLSListing> = {};
    for (const l of listings) {
      const key = l.ListingId ?? `${l.Latitude}-${l.Longitude}`;
      byId[key] = l;
    }
    return byId;
  }, [listings]);

  const onPopupClose = useCallback(() => setSelectedListing(null), []);

  // Initialise the map once
  useEffect(() => {
    if (mapContainerRef.current === null || mapRef.current !== null) return;

    mapRef.current = new MapBox(
      mapContainerRef.current,
      [center.lng, center.lat],
      { styleURL: INITIAL_STYLE.url, zoom: 12 },
      (instance) => {
        instance.getMap().addControl(new mapboxgl.NavigationControl());
      },
    );
  }, [center.lat, center.lng]);

  // Load pins and wire click handler once the map style is ready
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    const loadPins = () => {
      // Source
      if (!map.getSource(MAP_SOURCES.PINS)) {
        map.addSource(MAP_SOURCES.PINS, {
          type: 'geojson',
          data: { type: 'FeatureCollection', features },
        });
      } else {
        const src = map.getSource(MAP_SOURCES.PINS);
        if (src?.type === 'geojson') {
          src.setData({ type: 'FeatureCollection', features });
        }
      }

      // Layer
      if (!map.getLayer(MAP_LAYERS.PINS)) {
        map.addLayer({
          id: MAP_LAYERS.PINS,
          type: 'symbol',
          source: MAP_SOURCES.PINS,
          layout: {
            'icon-image': ['get', 'icon'],
            'icon-size': 1,
            'icon-allow-overlap': true,
          },
        });
      }

      // Click handler
      const handleClick = (e: mapboxgl.MapLayerMouseEvent) => {
        const feature = e.features?.[0];
        if (!feature) return;
        const id = feature.properties?.id as string | undefined;
        if (!id) return;
        setSelectedListing(listingsById[id] ?? null);
      };

      const handleMouseEnter = () => {
        map.getCanvas().style.cursor = 'pointer';
      };
      const handleMouseLeave = () => {
        map.getCanvas().style.cursor = '';
      };

      map.on('click', MAP_LAYERS.PINS, handleClick);
      map.on('mouseenter', MAP_LAYERS.PINS, handleMouseEnter);
      map.on('mouseleave', MAP_LAYERS.PINS, handleMouseLeave);

      // Close popup when clicking outside a pin
      const handleMapClick = (e: mapboxgl.MapMouseEvent) => {
        const pinFeatures = map.queryRenderedFeatures(e.point, { layers: [MAP_LAYERS.PINS] });
        if (!pinFeatures.length) setSelectedListing(null);
      };
      map.on('click', handleMapClick);

      return () => {
        map.off('click', MAP_LAYERS.PINS, handleClick);
        map.off('mouseenter', MAP_LAYERS.PINS, handleMouseEnter);
        map.off('mouseleave', MAP_LAYERS.PINS, handleMouseLeave);
        map.off('click', handleMapClick);
      };
    };

    if (map.loaded()) {
      return loadPins();
    } else {
      let cleanup: (() => void) | undefined;
      const onLoad = () => {
        cleanup = loadPins();
      };
      map.on('load', onLoad);
      return () => {
        map.off('load', onLoad);
        cleanup?.();
      };
    }
  }, [features, listingsById]);

  return (
    <Flex flexDirection="column" gap="12px">
      <Typography variant="h4">Listings ({listings.length})</Typography>

      <Block position="relative" height="400px" borderRadius="8px" overflow="hidden">
        {/* Bare map container — no React children inside */}
        <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />

        {/* Overlays positioned on top of the map */}
        <Block
          position="absolute"
          bottom="10px"
          right="10px"
          zIndex="1"
          backgroundColor="surface2"
          padding="8px 16px"
          borderRadius="4px"
        >
          <StyleRadio mapRef={mapRef} style={mapStyle} onStyleChange={setMapStyle} />
        </Block>

        {selectedListing != null &&
          selectedListing.Latitude != null &&
          selectedListing.Longitude != null && (
            <MapPopup
              map={mapRef.current?.getMap()}
              lat={selectedListing.Latitude}
              lng={selectedListing.Longitude}
              onClose={onPopupClose}
            >
              <PropertyTooltip {...propertyTooltipPropsFromListing(selectedListing)} />
            </MapPopup>
          )}
      </Block>
    </Flex>
  );
};
